import {AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core'
import {MatTableDataSource} from '@angular/material/table'
import {ManagementInformationService, MiEvent} from '../../../services/management-information.service'
import {DateTime} from 'luxon'
import {Environment, ENVIRONMENTS, Region} from '../../../services/environment-service'
import {of, Subject} from 'rxjs'
import {debounceTime, distinctUntilChanged, map, mergeMap, switchMap, takeUntil, tap, toArray} from 'rxjs/operators'
// import {stringify} from 'csv/lib/sync'
import {MatPaginator} from '@angular/material/paginator'
import {MatSort} from '@angular/material/sort'
import {UntypedFormControl} from '@angular/forms'
import { unparse } from 'papaparse'
import saveAs from 'file-saver'
const equals = require('fast-deep-equal')

const environmentLookup = (account) => [ENVIRONMENTS.TOOLS,ENVIRONMENTS.STAGING, ENVIRONMENTS.UAT, ENVIRONMENTS.PROD].filter(env => !!env).find((v) => v && (v.account === account))

interface DocgenMiEvent extends MiEvent {
  source: 'inspire.lambda-ingest-product'
  detail: {
    latency: number
    requestId: string
    productIdentifier: string
    configPrefix: string
    stageSummary?: {
      analysePayload: {
        result: string
        duration: number
      },
      validatePayload: {
        result: string
        duration: number
      },
      transform: {
        result: string
        duration: number
      },
      enrich: {
        result: string
        duration: number
      },
      analyseEnriched: {
        result: string
        duration: number
      },
      validateEnriched: {
        result: string
        duration: number
      },
      generateDocument?: {
        result: string
        duration: number
      },
      renderDocument?: {
        result: string
        duration: number
      }
    }
  }
}

interface FlattenedDocgenMiEvent {
  timestamp: string
  date: string
  time: string
  environment: string
  region: string
  status: string
  sparrowRef: string
  sparrowTicket: string
  config: string
  latency: number
  validatePayload: string
  analysePayload: string
  transform: string
  enrich: string
  analyseEnriched: string
  validateEnriched: string
  renderDocument: string
  renderLatency: number
}

interface Request {
  environment: Environment
  fromDate: string
  toDate: string
}

@Component({
  selector: 'app-docgen-management-information',
  templateUrl: './docgen-management-information.component.html',
  styleUrls: ['./docgen-management-information.component.scss'],
})
export class DocgenManagementInformationComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  loading = true
  cancelRequests = new Subject<void>()

  @Input() environment: Environment
  @Input() fromDate: string
  @Input() toDate: string

  filter = new UntypedFormControl('')

  results: FlattenedDocgenMiEvent[] = []
  columns: string[] = [
    'date',
    'time',
    'environment',
    'region',
    'status',
    'sparrowRef',
    'sparrowTicket',
    'config',
    'latency',
    'validatePayload',
    'analysePayload',
    'transform',
    'enrich',
    'analyseEnriched',
    'validateEnriched',
    'renderDocument',
    'renderLatency',
  ]

  dataSource = new MatTableDataSource<FlattenedDocgenMiEvent>()
  requests = new Subject<Request>()

  constructor(private managementInformationService: ManagementInformationService) {
  }

  @ViewChild(MatPaginator) paginator: MatPaginator
  @ViewChild(MatSort) sort: MatSort

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator
    this.dataSource.sort = this.sort
  }

  ngOnInit(): void {
    // this.loading = true
    console.log(ENVIRONMENTS)
    this.requests
      .pipe(
        takeUntil(this.cancelRequests),
        debounceTime(1000),
        distinctUntilChanged(equals),
        tap(_ => this.loading = true),
        tap(request => {
          console.log('Processing: ')
          console.dir(request)
        }),
        switchMap((request: Request) => {
          if (request.environment && request.fromDate) {
            return of(...request.environment.regions)
              .pipe(
                mergeMap((region: Region) => {
                  console.log('Fetching data for ' + region.display)
                  const dateRange = request.fromDate + (request.toDate ? '/' + request.toDate : '')
                  return this.managementInformationService.getManagementInformation<DocgenMiEvent[]>(
                    request.environment.id,
                    region.id,
                    'lambda-ingest-product',
                    dateRange)
                    .pipe(
                      tap(results => {
                        console.log('Fetched Mi Data For ' + request.environment.display + ' in ' + region.display)
                        console.dir(results)
                      }),
                    )
                }),
                toArray(),
                tap((results: DocgenMiEvent[][]) => {
                  console.log('Raw Mi Data')
                  console.dir(results)
                }),
                map((events: DocgenMiEvent[][]): DocgenMiEvent[] => {
                  return events.reduce((r, a) => r.concat(...a), <DocgenMiEvent[]> [])
                  // fileName: `${request.report}-${request.environment.display}-${request.fromDate}${request.toDate ? '-' + request.toDate : ''}.csv`
                  // }
                }),
                tap((results: any[]) => {
                  console.log('Combined Mi Data')
                  console.dir(results)
                }),
                tap(_ => this.loading = false),
              )
          } else {
            return []
          }
        }),
      )
      .subscribe((events: DocgenMiEvent[]) => {
        const flattened = events.map(e => this.format(e))
        flattened.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
        this.results = flattened
        this.dataSource.data = this.results
        this.dataSource.sort = this.sort
      })
    this.filter.valueChanges
      .pipe(
        takeUntil(this.cancelRequests),
      )
      .subscribe(text => {
        this.dataSource.filter = text
      })
    this.ngOnChanges(undefined)
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.requests.next({
      environment: this.environment,
      fromDate: this.fromDate,
      toDate: this.toDate,
    })
  }

  ngOnDestroy(): void {
    this.cancelRequests.next()
  }

  format(e: DocgenMiEvent): FlattenedDocgenMiEvent {

    const environment = environmentLookup ? environmentLookup(e.account).display : e.account
    const region = e.region === 'eu-central-1' ? 'Frankfurt' : 'London'
    const detail = e.detail
    const stageSummary = detail.stageSummary
    return {
      timestamp: e.time,
      date: DateTime.fromISO(e.time).toFormat('dd/MM/yyyy'),
      time: DateTime.fromISO(e.time).toFormat('HH:mm:ss'),
      environment: environment,
      region: region,
      status: e['detail-type'],
      sparrowRef: detail.productIdentifier,
      sparrowTicket: detail.requestId,
      config: detail.configPrefix,
      latency: detail.latency,
      validatePayload: stageSummary && stageSummary.validatePayload?.result,
      analysePayload: stageSummary && stageSummary.analysePayload?.result,
      transform: stageSummary && stageSummary.transform?.result,
      enrich: stageSummary && stageSummary.enrich?.result,
      analyseEnriched: stageSummary && stageSummary.analyseEnriched?.result,
      validateEnriched: stageSummary && stageSummary.validateEnriched?.result,
      renderDocument: stageSummary && (stageSummary.renderDocument?.result || stageSummary.generateDocument?.result),
      renderLatency: stageSummary?.renderDocument?.duration,
    }
  }

  downloadCsv() {
    const csv = unparse(this.results, {
      header: true,
    })
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8'})
    const filename = ('mi-docgen-' + this.environment.display.toLowerCase() + '-' + this.fromDate + (this.toDate ? '-to-' + this.toDate : '') + '.csv')
    console.log('Saving: ' + filename)
    saveAs(blob, filename)
  }

}
